import React, { useState } from 'react';
import { Avatar, IconButton, Tooltip } from '@mui/material';
import { ReceiptLong } from '@mui/icons-material';
import { Trade, wearMap } from '../../../../types/trades';
import { formatMoney, moneyFromMinor } from '../../../utils/UnitConversion.utils';
import { RARITY_COLORS, RarityGrade } from '../../../utils/RarityColors';
import ItemDetailLightModal from '../../../skin-db/offer-details/ItemDetailLightModal';
import { ItemDTO } from '../../../user/on-sale/types';

type TradeGroupItemListProps = {
    group: Trade[];
    currencyRate: number;
    currencySymbol: string;
    /**
     * 'trade' -> per-item "View Transaction" icon (single trade partner).
     * 'buy'-> per-item seller avatar, because a buy group can span multiple sellers.
     */
    variant?: 'trade' | 'buy';
    onNavigate: (tradeId: string) => void;
};

const TradeGroupItemList: React.FC<TradeGroupItemListProps> = ({
                                                                   group,
                                                                   currencyRate,
                                                                   currencySymbol,
                                                                   variant = 'trade',
                                                                   onNavigate
                                                               }) => {
    const [selectedItemForModal, setSelectedItemForModal] = useState<ItemDTO | null>(null);

    return (
        <>
            <div className="max-h-[166px] overflow-y-auto pr-1 space-y-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200 dark:scrollbar-thumb-neutral-700">
                {group.map(trade => {
                    const itemPrice = formatMoney(moneyFromMinor(trade.priceMinor) * currencyRate);
                    const rarityColor = RARITY_COLORS[trade.item.details?.rarity as RarityGrade] ?? '#ffffff';
                    return (
                        <div key={trade.id}
                             className="flex justify-between items-center p-2 rounded-lg bg-gray-50/30 dark:bg-neutral-900/20 border border-gray-100/50 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-neutral-900/40 transition-colors">
                            <div
                                onClick={() => setSelectedItemForModal(trade.item)}
                                className="flex items-center gap-3 cursor-pointer group/item"
                            >
                                <div className="relative flex items-center shrink-0">
                                    <div style={{ width: 3, height: 32, background: rarityColor }} />
                                    <div className="relative ml-1">
                                        <img src={trade.item.catalog?.imageLink ?? ''}
                                             className="w-8 h-8 object-contain z-10 relative drop-shadow-md transition-transform duration-200 group-hover/item:scale-105"
                                             alt="item" />
                                        <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-[44px] h-[32px] z-0"
                                             style={{ background: `linear-gradient(to right, ${rarityColor}22, transparent)` }}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-800 dark:text-gray-100 group-hover/item:text-dark-primary transition-colors">{trade.item.catalog?.groupName ?? 'Unknown Item'}</p>
                                    <div className="flex gap-1 text-[10px] text-gray-500 dark:text-gray-400">
                                        {trade.item.catalog?.isStattrak && <span className="text-orange-500 font-bold">ST</span>}
                                        {trade.item.catalog?.wearName && <span>{wearMap[trade.item.catalog.wearName] ?? ''}</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs font-mono font-bold text-gray-600 dark:text-gray-400">{currencySymbol}{itemPrice}</span>
                                {variant === 'buy' ? (
                                    <Tooltip title="Seller" arrow>
                                        <Avatar src={trade.sellerSteamAvatarUrl ?? ''}
                                                className="!w-6 !h-6 ring-1 ring-gray-200 dark:ring-neutral-700" />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="View Transaction" arrow>
                                        <IconButton onClick={() => onNavigate(trade.id)}
                                                    size="small"
                                                    className="!p-1 !bg-gray-50 dark:!bg-white/5 hover:!bg-blue-50 dark:hover:!bg-blue-500/10 !text-gray-400 hover:!text-blue-500 transition-colors"
                                                    sx={{ borderRadius: '6px' }}>
                                            <ReceiptLong sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <ItemDetailLightModal
                open={!!selectedItemForModal}
                onClose={() => setSelectedItemForModal(null)}
                item={selectedItemForModal}
            />
        </>
    );
};

export default TradeGroupItemList;
